import { useState, useContext, useCallback } from "react";
import {
  Card,
  CardContent,
  TextField,
  Button as ButtonUi,
  Box,
  InputLabel,
  Select,
  MenuItem,
  TextareaAutosize,
  SelectChangeEvent,
  Grid,
  Modal,
  IconButton,
  Typography,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Button } from "../Button";
import { Header } from "../Header";
import {
  Container,
  Form,
  FormField,
  Input,
  SubTitle,
  Title,
  Text,
  Load,
  LoadFile,
  AddNewFile,
  ContainerForm,
  ContainerTextArea,
} from "./style";
import { Spinner } from "../../ui/Spinner";
import { Close, UploadFile } from "@mui/icons-material";
import { cnpjMask, mascaraMoeda } from "../../utils/mask";
import {
  apiOperation,
  apiExtractor,
  headers,
  apiPetro,
} from "../../services/api";
import { Error } from "../Error";
import { Loading } from "../Loading";
import icoAdd from "../../assets/add.svg";
import { useNavigate } from "react-router-dom";
import ErrorModal from "../ErrorModal";
import { getSessionUser } from "../../utils/users";
import { useEffect } from "react";
import { IUser } from "../Login/ILogin";
import { Empresas } from "../../utils/empresa";
import React from "react";
import { Listar } from "../../models/analise";
import { HeaderModal } from "../Results/style";
import { useUnsavedChanges } from "../../data/analiseContext";
import { NumericFormat } from "react-number-format";
import { Resultado } from "../../models/base/basic";
import { Conta } from "../../models/notouch/conta";
import { ClienteResponse } from "../../models/Petro/ClienteResponse ";

interface ISegmento {
  segmentoId: string;
  empresaId: string;
  risco: string;
  segmento: string;
  porcentagem: number;
}

interface IArquivoGenerico {
  id: number;
  load?: boolean;
  name?: string | null;
  tipoDocumento: string;
  file?: File;
}

interface IDocumento {
  id: number;
  nome: string;
  nomePersonalizado: string;
  arquivo: File;
  tipoDocumento: string;
}

interface IFormfind {
  cnpj: string;
  valorSolicitado: string;
  classificacaoInterna: EclassificacaoInterna | "";
  parecerComercial: string;
  codigoGpEconomico: string;
  vigenciaCredito: string;
  parecerAssessor: string;
}

enum EclassificacaoInterna {
  Otimo = "otimo",
  Bom = "bom",
  Ruim = "ruim",
}

let usuarioLogado: IUser | null = null;

interface response {
  erro: boolean;
  erros: string[];
  statusCode: number;
}

interface HistoryResult {
  found: boolean;
  analiseId?: string;
}

const InputStyled = { marginBottom: "8px", width: "100%" };

// Componente para cada item de documento
interface DocumentoItemProps {
  documento: IDocumento;
  onRemover: (id: number) => void;
  onUpload: (id: number, arquivo: File) => void;
  onNomeChange: (id: number, nome: string) => void;
  hasError: boolean;
}

const DocumentoItem: React.FC<DocumentoItemProps> = ({
  documento,
  onRemover,
  onUpload,
  onNomeChange,
  hasError,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      onUpload(documento.id, files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      onUpload(documento.id, files[0]);
    }
    e.target.value = "";
  };

  const formatarTamanhoArquivo = (bytes: number): string => {
    if (bytes === 0) return "Sem arquivo";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Box
      sx={{ display: "flex", alignItems: "center", marginBottom: 1, gap: 1 }}
    >
      {/* Campo de nome personalizado mais fluido */}
      <TextField
        size="small"
        variant="outlined"
        placeholder="Digite o nome do documento"
        value={documento.nomePersonalizado}
        onChange={(e) => onNomeChange(documento.id, e.target.value)}
        error={hasError}
        helperText={hasError ? "Nome é obrigatório" : ""}
        sx={{
          flexGrow: 1,
          minWidth: "150px",
          "& .MuiOutlinedInput-root": {
            height: "36px",
            fontSize: "0.875rem",
          },
        }}
        onClick={(e) => e.stopPropagation()}
      />

      {/* Área de upload quadrada */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "36px",
          height: "36px",
          border: isDragOver
            ? "2px dashed var(--info)"
            : documento.arquivo.size > 0
              ? "1px solid var(--info)"
              : "1px dashed var(--border-default)",
          borderRadius: 1,
          backgroundColor: isDragOver
            ? "var(--info-surface)"
            : documento.arquivo.size > 0
              ? "var(--info-surface)"
              : "var(--surface-canvas)",
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor:
              documento.arquivo.size > 0
                ? "var(--info-surface)"
                : "var(--surface-sunken)",
            borderColor: "var(--info)",
          },
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() =>
          document.getElementById(`file-input-${documento.id}`)?.click()
        }
        title={
          documento.arquivo.size > 0
            ? `${documento.nome} (${formatarTamanhoArquivo(documento.arquivo.size)})`
            : "Clique ou arraste um arquivo"
        }
      >
        <input
          id={`file-input-${documento.id}`}
          type="file"
          accept="*/*"
          style={{ display: "none" }}
          onChange={handleFileChange}
        />

        {documento.arquivo.size > 0 ? (
          <UploadFile sx={{ color: "var(--info)", fontSize: 20 }} />
        ) : (
          <UploadFile sx={{ color: "var(--text-disabled)", fontSize: 18 }} />
        )}
      </Box>

      {/* Botão remover */}
      <IconButton
        size="small"
        onClick={() => onRemover(documento.id)}
        sx={{
          color: "var(--error)",
          width: 24,
          height: 24,
          "&:hover": {
            backgroundColor: "var(--error-surface)",
          },
        }}
      >
        <Close sx={{ fontSize: 12 }} />
      </IconButton>
    </Box>
  );
};

export const NovaAnalise = () => {
  let navigate = useNavigate();
  const [form, setForm] = useState<IFormfind>({
    cnpj: "",
    valorSolicitado: "",
    classificacaoInterna: "",
    parecerComercial: "",
    codigoGpEconomico: "",
    vigenciaCredito: "",
    parecerAssessor: "",
  });
  const [hasLoad, setHasLoad] = useState<boolean>(false);
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [messagesLoading, setMessagesLoading] = useState<string>("");
  const [isValid, setIsValid] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [grupoEconomico, setGrupoEconomico] = useState<
    { cnpjOuCpf: string; razaoSocial: string }[]
  >([]);
  const [valorSolicitadoError, setValorSolicitadoError] = useState(false);
  const [codigoGpEconomico, setCodigoGpEconomico] = useState<string>("");
  const [analisar, setAnalisar] = useState<boolean>(false);
  const [documentos, setDocumentos] = useState<IDocumento[]>([]);
  const [nextDocId, setNextDocId] = useState<number>(1);
  const [documentosComErro, setDocumentosComErro] = useState<number[]>([]);
  const [modalState, setModalState] = useState({
    open: false,
    title: "",
    message: "",
    existingId: "",
    buttonContinue: "",
    buttonExisting: "",
  });

  const [errorApi, setErrorApi] = useState({
    message: "",
    title: "",
    state: false,
  });
  const validateCnpjCpf = (value: string) => {
    const cleanValue = value.replace(/[^\d]+/g, ""); // Remove todos os caracteres não numéricos
    let isValid = false;

    if (cleanValue.length === 11) {
      isValid = isCpf(cleanValue);
    } else if (cleanValue.length === 14) {
      isValid = isCnpj(cleanValue);
    }

    setIsValid(isValid);
    setErrorMessage(isValid ? "" : "CNPJ/CPF não é válido.");

    return isValid;
  };

  const style = {
    position: "absolute" as "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "30%",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    borderRadius: "18px",
  };

  const openModalAnaliseRetroativa = (existingId: string) => {
    setModalState({
      open: true,
      title: "Análise Recente Encontrada",
      message:
        "Já existe uma análise recente (menos de 30 dias) para este CNPJ/CPF. Caso deseje prosseguir com uma nova análise, haverá cobrança. \n Deseja continuar com uma nova?",
      existingId: existingId,
      buttonContinue: "Continuar com a análise",
      buttonExisting: "Ver análise existente",
    });
  };

  const SetEmptyModal = () => {
    setModalState({
      open: false,
      title: "",
      message: "",
      existingId: "",
      buttonExisting: "",
      buttonContinue: "",
    });
  };

  const handleClose = () => {
    SetEmptyModal();
  };

  const handleContinue = () => {
    handleClose();
    obterDadosPetro();
  };

  const handleViewExisting = () => {
    window.location.href = `/resultados/${modalState.existingId}`;
  };

  const getHistory = async (params = ""): Promise<HistoryResult> => {
    try {
      const config = {
        headers: headers(),
      };
      setLoadingModal(true);
      setMessagesLoading("Verificando Analise Retroativa...");
      let urlString = `analise/listar
					?pagina=1
					&registrosPorPagina=10&cnpjOuCpf=${form.cnpj.replace(/\D/g, "")}`;

      const { data } = await apiOperation.get<Listar>(urlString, config);

      setLoadingModal(false);
      if (!data.erro && data.valor.dados.length > 0) {
        const now = new Date();
        for (const analise of data.valor.dados) {
          const dataInclusao = new Date(analise.dataInclusao);
          const diffDays =
            (now.getTime() - dataInclusao.getTime()) / (1000 * 60 * 60 * 24);

          if (diffDays < 30) {
            return { found: true, analiseId: analise.analiseId };
          }
        }
      }
      return { found: false };
    } catch (error: any) {
      setLoadingModal(false);
      return { found: false };
    }
  };

  const ValidarAnaliseRetroativa = async () => {
    const valorLimpo = form.valorSolicitado.replace(/[^\d]+/g, "");
    if (!form.valorSolicitado.trim() || Number(valorLimpo) === 0) {
      setValorSolicitadoError(true);
      setErrorApi({
        state: true,
        message: "O campo Valor Solicitado é obrigatório.",
        title: "Campo Obrigatório",
      });
      return;
    }
    var result = await getHistory();
    if (result.found) openModalAnaliseRetroativa(result.analiseId ?? "");
    else await obterDadosPetro();
  };

  const handleChange = (event: any) => {
    const { name, value } = event.target;

    if (event.target.name === "cnpj") {
      var isValid = validateCnpjCpf(value);

      setIsValid(isValid);
      setErrorMessage(isValid ? "" : "CNPJ/CPF não é válido.");
    }

    if (event.target.name === "cnpjSolidario") {
      var isValid = validateCnpjCpf(value);

      setErrorMessage(isValid ? "" : "CNPJ/CPF não é válido.");
    }

    if (event.target.name === "valorSolicitado") {
      setValorSolicitadoError(false);
    }

    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const handleChangeClassificacaoInterna = (event: any) => {
    setForm({ ...form, classificacaoInterna: event.target.value });
  };

  useEffect(() => {
    usuarioLogado = getSessionUser();
  }, []);

  const isCnpj = (cnpj: string): boolean => {
    const multiplicador1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const multiplicador2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

    let soma: number;
    let resto: number;
    let digito: string;
    let tempCnpj: string;

    cnpj = cnpj.trim().replace(/[^\d]+/g, "");

    if (cnpj.length !== 14) return false;

    tempCnpj = cnpj.substring(0, 12);
    soma = 0;

    for (let i = 0; i < 12; i++) {
      soma += parseInt(tempCnpj[i]) * multiplicador1[i];
    }

    resto = soma % 11;
    resto = resto < 2 ? 0 : 11 - resto;
    digito = resto.toString();
    tempCnpj = tempCnpj + digito;
    soma = 0;

    for (let i = 0; i < 13; i++) {
      soma += parseInt(tempCnpj[i]) * multiplicador2[i];
    }

    resto = soma % 11;
    resto = resto < 2 ? 0 : 11 - resto;
    digito = digito + resto.toString();

    return cnpj.endsWith(digito);
  };

  const isCpf = (cpf: string): boolean => {
    const multiplicador1 = [10, 9, 8, 7, 6, 5, 4, 3, 2];
    const multiplicador2 = [11, 10, 9, 8, 7, 6, 5, 4, 3, 2];

    let soma: number;
    let resto: number;
    let digito: string;
    let tempCpf: string;

    cpf = cpf.trim().replace(/[^\d]+/g, "");

    if (cpf.length !== 11) return false;

    tempCpf = cpf.substring(0, 9);
    soma = 0;

    for (let i = 0; i < 9; i++) {
      soma += parseInt(tempCpf[i]) * multiplicador1[i];
    }

    resto = soma % 11;
    resto = resto < 2 ? 0 : 11 - resto;
    digito = resto.toString();
    tempCpf = tempCpf + digito;
    soma = 0;

    for (let i = 0; i < 10; i++) {
      soma += parseInt(tempCpf[i]) * multiplicador2[i];
    }

    resto = soma % 11;
    resto = resto < 2 ? 0 : 11 - resto;
    digito = digito + resto.toString();

    return cpf.endsWith(digito);
  };

  const obterDadosPetro = async () => {
    setLoadingModal(true);
    setMessagesLoading("Obtendo dados do Petro...");

    try {
      var url = `/analise/consultar?cnpj=${form.cnpj}`;

      const config = {
        headers: headers(),
      };

      const response = await apiPetro.get<Resultado<ClienteResponse>>(
        url,
        config,
      );

      setLoadingModal(false);

      if (response.data.erro) {
        setErrorApi({
          state: true,
          message: response.data.erros.join(", "),
          title: "Erro ao obter dados do ERP",
        });
        return;
      }

      setGrupoEconomico(
        (response.data.valor.grupoEconomico || []).map((item: any) => ({
          cnpjOuCpf: item.cnpjOuCpf || "",
          razaoSocial: item.razaoSocial || item.razao_social || "",
        })),
      );

      // Define o código do grupo econômico no formulário
      setForm((prevForm) => ({
        ...prevForm,
        codigoGpEconomico: response.data.valor.codigoGrupo || "",
      }));

      setAnalisar(true);
    } catch (error: any) {
      setLoadingModal(false);
      setMessagesLoading("");
      setErrorApi({
        state: true,
        message: error.response?.data,
        title: "Erro",
      });
      return;
    }
  };

  const adicionarDocumento = (event?: React.ChangeEvent<HTMLInputElement>) => {
    if (event?.target.files && event.target.files[0]) {
      // Se um arquivo foi selecionado, criar documento com arquivo
      const novoDocumento: IDocumento = {
        id: nextDocId,
        nome: event.target.files[0].name,
        nomePersonalizado: "",
        arquivo: event.target.files[0],
        tipoDocumento: "Documento Geral",
      };
      setDocumentos([...documentos, novoDocumento]);
      setNextDocId(nextDocId + 1);
      event.target.value = "";
    } else {
      // Criar documento vazio para upload posterior
      const arquivoVazio = new File([""], `Documento ${nextDocId}`, {
        type: "text/plain",
      });
      const novoDocumento: IDocumento = {
        id: nextDocId,
        nome: `Clique para fazer upload`,
        nomePersonalizado: "",
        arquivo: arquivoVazio,
        tipoDocumento: "Documento Geral",
      };
      setDocumentos([...documentos, novoDocumento]);
      setNextDocId(nextDocId + 1);
    }
  };

  const removerDocumento = (id: number) => {
    setDocumentos(documentos.filter((doc) => doc.id !== id));
  };

  const atualizarDocumento = (id: number, novoArquivo: File) => {
    setDocumentos(
      documentos.map((doc) =>
        doc.id === id
          ? { ...doc, nome: novoArquivo.name, arquivo: novoArquivo }
          : doc,
      ),
    );
  };

  /** Retorna os IDs dos documentos com nome vazio ou com nome duplicado (case-insensitive). */
  const calcularIdsComErro = useCallback((lista: IDocumento[]): number[] => {
    const idsComNomeVazio = lista
      .filter((doc) => !doc.nomePersonalizado.trim())
      .map((doc) => doc.id);

    const nomeParaIds = new Map<string, number[]>();
    lista.forEach((doc) => {
      const nome = doc.nomePersonalizado.trim().toLowerCase();
      if (!nome) return;
      if (!nomeParaIds.has(nome)) nomeParaIds.set(nome, []);
      nomeParaIds.get(nome)!.push(doc.id);
    });

    const idsDuplicados = Array.from(nomeParaIds.values())
      .filter((ids) => ids.length > 1)
      .flat();

    return Array.from(new Set([...idsComNomeVazio, ...idsDuplicados]));
  }, []);

  const atualizarNomePersonalizado = (id: number, novoNome: string) => {
    const proximoDocumentos = documentos.map((doc) =>
      doc.id === id ? { ...doc, nomePersonalizado: novoNome } : doc,
    );
    setDocumentos(proximoDocumentos);
    setDocumentosComErro(calcularIdsComErro(proximoDocumentos));
  };

  const validarDocumentos = (): boolean => {
    const documentosInvalidos = calcularIdsComErro(documentos);
    setDocumentosComErro(documentosInvalidos);
    return documentosInvalidos.length === 0;
  };

  const formatarTamanhoArquivo = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const uploadDocumentos = async (): Promise<string[]> => {
    const documentosValidos = documentos.filter(
      (doc) => doc.arquivo.size > 0 && doc.nomePersonalizado.trim(),
    );

    if (documentosValidos.length === 0) {
      return [];
    }

    setMessagesLoading("Fazendo upload dos documentos...");

    try {
      const formData = new FormData();

      // Adicionar CNPJ como campo obrigatório
      formData.append("cnpj", form.cnpj);

      // Adicionar cada arquivo ao FormData com o campo 'files'
      documentosValidos.forEach((doc, index) => {
        // Extrair extensão do arquivo original
        const nomeOriginal = doc.arquivo.name;
        const ultimoPonto = nomeOriginal.lastIndexOf(".");
        const extensao =
          ultimoPonto !== -1 ? nomeOriginal.substring(ultimoPonto) : "";

        // Verificar se o nome personalizado já tem extensão
        const nomePersonalizado = doc.nomePersonalizado;
        const temExtensao =
          nomePersonalizado.includes(".") &&
          nomePersonalizado.lastIndexOf(".") > nomePersonalizado.length - 6;

        // Montar nome final: nome personalizado + extensão original (se não tiver)
        const nomeFinal = temExtensao
          ? nomePersonalizado
          : nomePersonalizado + extensao;

        // Criar arquivo com nome personalizado + extensão
        const arquivoComNomePersonalizado = new File([doc.arquivo], nomeFinal, {
          type: doc.arquivo.type,
          lastModified: doc.arquivo.lastModified,
        });

        formData.append("files", arquivoComNomePersonalizado);
      });

      // Headers conforme o curl - sem Content-Type manual para FormData
      const config = {
        headers: {
          accept: "application/json",
          Authorization: headers().Authorization,
        },
      };

      console.log("Fazendo upload com FormData: files + cnpj");
      const response = await apiExtractor.post(
        "/documento/upload-petro",
        formData,
        config,
      );

      return response.data;
    } catch (error: any) {
      console.error("Erro detalhado:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
      });

      throw (
        error.response?.data ||
        error.message ||
        "Erro ao fazer upload dos documentos"
      );
    }
  };

  const enviarAnalise = async () => {
    // Validar documentos antes de enviar
    if (documentos.length > 0 && !validarDocumentos()) {
      setErrorApi({
        state: true,
        message:
          "Preencha o nome de todos os documentos e use nomes diferentes para cada um. Não é permitido repetir o mesmo nome.",
        title: "Validação de Documentos",
      });
      return;
    }

    setLoadingModal(true);
    setMessagesLoading("Enviando análise...");

    try {
      let linksDocumentos: string[] = [];

      // Verificar se há documentos obrigatórios
      if (documentos.length === 0) {
        setLoadingModal(false);
        setErrorApi({
          state: true,
          message:
            "Favor anexar a documentação obrigatória para limite de credito.",
          title: "Documentação Obrigatória",
        });
        return;
      }

      // Se houver documentos, fazer upload primeiro
      if (documentos.length > 0) {
        linksDocumentos = await uploadDocumentos();
      }

      // Preparar payload da análise
      const payload = {
        cnpjOuCpf: form.cnpj,
        valorSolicitado: form.valorSolicitado,
        classificacaoInterna: form.classificacaoInterna,
        vigenciaCredito: form.vigenciaCredito,
        parecerAssessor: form.parecerAssessor,
        codigoGpEconomico: form.codigoGpEconomico,
        ...(linksDocumentos.length > 0 && { urlDocumentos: linksDocumentos }),
      };

      setMessagesLoading("Enviando análise...");

      const config = {
        headers: headers(),
      };

      await apiPetro.post("/analise/analisar", payload, config);

      setLoadingModal(false);
      navigate("/historico");
    } catch (error: any) {
      setLoadingModal(false);
      setMessagesLoading("");
      debugger;
      setErrorApi({
        state: true,
        message: error.response?.data,
        title: "Erro",
      });
    }
  };

  return (
    <>
      <Header />
      <Loading
        mode="blurry"
        hidden={!loadingModal}
        messages={messagesLoading}
      />
      <Container>
        <Title>Nova Análise de Crédito</Title>

        <Card sx={{ minWidth: 275 }}>
          <CardContent>
            <div>
              <SubTitle>Digite o CNPJ ou CPF que deseja pesquisar</SubTitle>
              <ContainerForm>
                <div style={{ padding: "10px" }}>
                  <TextField
                    id="cnpj"
                    label="CNPJ ou CPF"
                    variant="outlined"
                    name="cnpj"
                    type="text"
                    style={InputStyled}
                    value={cnpjMask(form.cnpj)}
                    onChange={handleChange}
                    error={!isValid}
                    helperText={!isValid && errorMessage}
                  />
                </div>
                <div style={{ padding: "10px" }}>
                  <TextField
                    required
                    id="valorSolicitado"
                    label="Valor Solicitado"
                    variant="outlined"
                    name="valorSolicitado"
                    type={"text"}
                    style={InputStyled}
                    value={form.valorSolicitado}
                    onChange={handleChange}
                    onInput={mascaraMoeda}
                    error={valorSolicitadoError}
                    helperText={
                      valorSolicitadoError &&
                      "O campo Valor Solicitado é obrigatório."
                    }
                  />
                </div>
                <div style={{ width: "300px", padding: "10px" }}>
                  <TextField
                    select
                    fullWidth
                    id="classificacaoInterna"
                    label="Classificação Comercial do Cliente"
                    name="classificacaoInterna"
                    variant="outlined"
                    style={InputStyled}
                    value={form.classificacaoInterna}
                    onChange={handleChangeClassificacaoInterna}
                  >
                    <MenuItem value={EclassificacaoInterna.Otimo}>
                      <Tooltip
                        title={
                          <Box
                            sx={{
                              padding: 0,
                              margin: 0,
                              "& ul": { paddingLeft: "20px" },
                              "& li": {
                                listStyleType: "disc",
                                display: "list-item",
                                marginLeft: "10px",
                              },
                            }}
                          >
                            <ul
                              style={{
                                paddingLeft: "20px",
                                marginTop: "5px",
                                marginBottom: "5px",
                              }}
                            >
                              <li
                                style={{
                                  listStyleType: "disc",
                                  display: "list-item",
                                }}
                              >
                                Histórico de pagamento: considera um cliente sem
                                histórico de inadimplência
                              </li>
                              <br />
                              <li
                                style={{
                                  listStyleType: "disc",
                                  display: "list-item",
                                }}
                              >
                                Recorrência comercial: considera um cliente que
                                realiza compras de forma recorrente e frequente
                              </li>
                              <br />
                              <li
                                style={{
                                  listStyleType: "disc",
                                  display: "list-item",
                                }}
                              >
                                Tempo de cadastro: considera um cliente com um
                                relacionamento superior a 2 anos
                              </li>
                            </ul>
                          </Box>
                        }
                        arrow
                        placement="right"
                      >
                        <span>Estratégico</span>
                      </Tooltip>
                    </MenuItem>
                    <MenuItem value={EclassificacaoInterna.Bom}>
                      <Tooltip
                        title={
                          <Box
                            sx={{
                              padding: 0,
                              margin: 0,
                              "& ul": { paddingLeft: "20px" },
                              "& li": {
                                listStyleType: "disc",
                                display: "list-item",
                                marginLeft: "10px",
                              },
                            }}
                          >
                            <ul
                              style={{
                                paddingLeft: "20px",
                                marginTop: "5px",
                                marginBottom: "5px",
                              }}
                            >
                              <li
                                style={{
                                  listStyleType: "disc",
                                  display: "list-item",
                                }}
                              >
                                Histórico de pagamento: considera um cliente com
                                pequenos atrasos (8-30 dias) frequentes, mas que
                                sempre regulariza pendências
                              </li>
                              <br />
                              <li
                                style={{
                                  listStyleType: "disc",
                                  display: "list-item",
                                }}
                              >
                                Recorrência comercial: considera um cliente que
                                realiza compras esporádicas, mas com alguma
                                regularidade
                              </li>
                              <br />
                              <li
                                style={{
                                  listStyleType: "disc",
                                  display: "list-item",
                                }}
                              >
                                Tempo de cadastro: considera um cliente com um
                                relacionamento entre 1 e 2 anos
                              </li>
                            </ul>
                          </Box>
                        }
                        arrow
                        placement="right"
                      >
                        <span>Intermediário</span>
                      </Tooltip>
                    </MenuItem>
                    <MenuItem value={EclassificacaoInterna.Ruim}>
                      <Tooltip
                        title={
                          <Box
                            sx={{
                              padding: 0,
                              margin: 0,
                              "& ul": { paddingLeft: "20px" },
                              "& li": {
                                listStyleType: "disc",
                                display: "list-item",
                                marginLeft: "10px",
                              },
                            }}
                          >
                            <ul
                              style={{
                                paddingLeft: "20px",
                                marginTop: "5px",
                                marginBottom: "5px",
                              }}
                            >
                              <li
                                style={{
                                  listStyleType: "disc",
                                  display: "list-item",
                                }}
                              >
                                Histórico de pagamento: considera um cliente com
                                atrasos superiores a 30 dias ou histórico de
                                inadimplência
                              </li>
                              <br />
                              <li
                                style={{
                                  listStyleType: "disc",
                                  display: "list-item",
                                }}
                              >
                                Recorrência comercial: considera um cliente que
                                realiza compras muito esporádicas ou está
                                inativo.
                              </li>
                              <br />
                              <li
                                style={{
                                  listStyleType: "disc",
                                  display: "list-item",
                                }}
                              >
                                Tempo de cadastro: considera um cliente com um
                                relacionamento inferior a 1 ano
                              </li>
                            </ul>
                          </Box>
                        }
                        arrow
                        placement="right"
                      >
                        <span>Crítico</span>
                      </Tooltip>
                    </MenuItem>
                  </TextField>
                </div>

                <div style={{ padding: "10px" }}>
                  <TextField
                    id="vigenciaCredito"
                    label="Vigência do crédito"
                    variant="outlined"
                    name="vigenciaCredito"
                    type={"text"}
                    style={InputStyled}
                    value={form.vigenciaCredito}
                    onChange={handleChange}
                  />
                </div>

                <div style={{ padding: "10px" }}>
                  <TextField
                    id="codigoGpEconomico"
                    label="Código do grupo econômico"
                    variant="outlined"
                    name="codigoGpEconomico"
                    type={"text"}
                    style={InputStyled}
                    value={form.codigoGpEconomico}
                    onChange={handleChange}
                  />
                </div>
              </ContainerForm>

              {analisar && (
                <Box sx={{ marginTop: 2, marginBottom: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ marginBottom: 2, color: "var(--text-secondary)" }}
                  >
                    Documentos:
                  </Typography>

                  {/* Lista discreta de documentos */}
                  <Box sx={{ maxWidth: "600px" }}>
                    {documentos.map((documento) => (
                      <DocumentoItem
                        key={documento.id}
                        documento={documento}
                        onRemover={removerDocumento}
                        onUpload={atualizarDocumento}
                        onNomeChange={atualizarNomePersonalizado}
                        hasError={documentosComErro.includes(documento.id)}
                      />
                    ))}

                    {/* Botão para adicionar documento - aparece abaixo dos documentos */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        marginTop: 1,
                        gap: 1,
                      }}
                    >
                      {documentosComErro.length > 0 && (
                        <Typography
                          variant="body2"
                          sx={{ color: "error.main", fontWeight: 500 }}
                        >
                          Cada documento deve ter um nome diferente. Corrija os
                          nomes duplicados ou vazios.
                        </Typography>
                      )}
                      <Button
                        className="verify"
                        onClick={() => adicionarDocumento()}
                        sx={{
                          minWidth: "32px",
                          width: "32px",
                          height: "32px",
                          padding: 0,
                          fontSize: "24px",
                          fontWeight: "bold",
                        }}
                      >
                        Adicionar documento
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}

              <ContainerTextArea>
                <InputLabel>Parecer do Assessor</InputLabel>
                <TextareaAutosize
                  maxRows={12}
                  minRows={8}
                  id="parecerAssessor"
                  name="parecerAssessor"
                  aria-label="maximum height"
                  placeholder="Digite o parecer do Assessor"
                  value={form.parecerAssessor}
                  style={{ width: "100%" }}
                  onChange={handleChange}
                />
              </ContainerTextArea>

              <CardContent>
                <SubTitle>CNPJs do grupo econômico</SubTitle>

                {grupoEconomico.length > 0 ? (
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: 8,
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            border: "1px solid var(--border-default)",
                            padding: 4,
                          }}
                        >
                          CNPJ/CPF
                        </th>
                        <th
                          style={{
                            border: "1px solid var(--border-default)",
                            padding: 4,
                          }}
                        >
                          Razão Social
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {grupoEconomico.map((item, idx) => (
                        <tr key={idx}>
                          <td
                            style={{
                              border: "1px solid var(--border-default)",
                              padding: 4,
                            }}
                          >
                            {cnpjMask(item.cnpjOuCpf)}
                          </td>
                          <td
                            style={{
                              border: "1px solid var(--border-default)",
                              padding: 4,
                            }}
                          >
                            {item.razaoSocial}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div>
                    <span style={{ color: "var(--text-secondary)" }}>
                      Nenhum grupo econômico encontrado.
                    </span>
                  </div>
                )}
              </CardContent>

              {!analisar && (
                <div style={{ display: "flex" }}>
                  {isValid && (
                    <Button
                      className="verify"
                      onClick={() => {
                        ValidarAnaliseRetroativa();
                      }}
                      sx={{
                        width: "10.4em",
                      }}
                    >
                      Verificar
                    </Button>
                  )}
                </div>
              )}
              {analisar && (
                <div>
                  <Box style={{ display: "flex" }}>
                    <Button
                      className="verify"
                      variant="contained"
                      color="secondary"
                      sx={{
                        width: "10.4em",
                      }}
                      onClick={enviarAnalise}
                    >
                      Analisar
                    </Button>
                  </Box>
                </div>
              )}

              {hasLoad && (
                <Loading
                  mode="blurry"
                  hidden={!loadingModal}
                  messages={messagesLoading}
                />
              )}
            </div>
          </CardContent>
        </Card>
        <Modal
          open={modalState.open}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ minWidth: "60%" }}
        >
          <Box sx={style}>
            <HeaderModal>
              <SubTitle>{modalState.title}</SubTitle>
              <IconButton sx={{ marginBottom: "30px" }} onClick={handleClose}>
                <Close />
              </IconButton>
            </HeaderModal>
            <Box>
              <Typography>{modalState.message}</Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "end",
                  alignItems: "center",
                  paddingTop: "20px",
                }}
              >
                {modalState.buttonExisting && (
                  <ButtonUi
                    sx={{
                      width: "10.4em",
                      margin: "2%",
                    }}
                    color="secondary"
                    variant="contained"
                    onClick={handleViewExisting}
                  >
                    {modalState.buttonExisting}
                  </ButtonUi>
                )}

                {modalState.buttonContinue && (
                  <ButtonUi
                    sx={{
                      width: "10.4em",
                      margin: "2%",
                    }}
                    color="secondary"
                    variant="contained"
                    onClick={handleContinue}
                  >
                    {modalState.buttonContinue}
                  </ButtonUi>
                )}
              </Box>
            </Box>
          </Box>
        </Modal>
        <Error
          hasOpen={errorApi.state}
          handleClose={() => setErrorApi({ ...errorApi, state: false })}
          errorMsg={errorApi.message}
          title={errorApi.title}
        />
      </Container>
    </>
  );
};
